import React, { useEffect, useMemo } from 'react'
import ReactFlow, {
  Background,
  CoordinateExtent,
  getNodesBounds,
  Node,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from 'reactflow'
import { ScenarioElement } from '@integration-app/sdk'
import { ScenarioTemplateExtended } from 'routes/Scenarios/helpers/types'
import { buildNodesAndEdgesFromScenarioElements } from './build-elements'
import { SCENARIO_ELEMENT_NODE_TYPES, ScenarioDiagramNodeType } from './nodes'
import { SCENARIO_ELEMENT_EDGE_TYPES } from './edges'
import 'reactflow/dist/base.css'
import classes from './ScenarioDiagram.module.scss'

type Props = {
  connectors: ScenarioTemplateExtended['connectors']
  elements: ScenarioElement[]
  onExternalAppsClick?: () => void
  onElementClick?: (element: ScenarioElement) => void
}

export function ScenarioDiagram(props: Props) {
  return (
    <div className={classes.wrapper}>
      <div className={classes.pane}>
        <ReactFlowProvider>
          <Graph {...props} />
        </ReactFlowProvider>
      </div>
    </div>
  )
}

function Graph({
  connectors,
  elements,
  onElementClick,
  onExternalAppsClick,
}: Props) {
  const [nodes, setNodes] = useNodesState([])
  const [edges, setEdges] = useEdgesState([])

  useEffect(() => {
    const result = buildNodesAndEdgesFromScenarioElements(
      elements,
      connectors.slice(0, 8),
    )

    setNodes(result.nodes)
    setEdges(result.edges)
  }, [JSON.stringify({ elements, connectors })])

  const translateExtent = useTranslateExtent(nodes)

  return (
    <ReactFlow
      onNodeClick={(event, node) => {
        switch (node.type) {
          case ScenarioDiagramNodeType.ScenarioElement:
            onElementClick?.(node.data)
            break
          case ScenarioDiagramNodeType.ExternalApps:
            onExternalAppsClick?.()
            break
          case ScenarioDiagramNodeType.YourApp:
            console.log({ node, event })
            break
          default:
            break
        }
      }}
      nodeTypes={SCENARIO_ELEMENT_NODE_TYPES}
      edgeTypes={SCENARIO_ELEMENT_EDGE_TYPES}
      nodes={nodes}
      edges={edges}
      minZoom={0.85}
      nodesConnectable={false}
      translateExtent={translateExtent}
      defaultViewport={{ x: 0, y: 0, zoom: 1 }}
      fitView
      panOnScroll
    >
      <Background gap={15} size={1} />
    </ReactFlow>
  )
}

const PADDING_X = 100
const PADDING_Y = 150

function useTranslateExtent(nodes: Node[]) {
  return useMemo(() => {
    const rect = getNodesBounds(nodes)

    return [
      [rect.x - PADDING_X, rect.y - PADDING_Y], // [x0, y0]
      [rect.x + rect.width + PADDING_X, rect.y + rect.height + PADDING_Y], // [x1, y1]
    ]
  }, [JSON.stringify(nodes)]) as CoordinateExtent
}
